import {
  defaultLoadableState,
  type LoadableState,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { createContext, PropsWithChildren, useMemo, useState } from 'react';

type DummyFnType = () => void;

type ContextType = {
  giveawayIsLive: boolean;
  isSpinnerImmersive: boolean;
  isSpinnerReady: LoadableState<boolean>;
  openHotspot: (id: number) => void;
  openKeyFeatures: () => void;
  setIsSpinnerImmersive: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenHotspot: React.Dispatch<React.SetStateAction<DummyFnType>>;
  setOpenKeyFeatures: React.Dispatch<React.SetStateAction<DummyFnType>>;
};

const defaultContext: ContextType = {
  giveawayIsLive: false,
  isSpinnerImmersive: false,
  isSpinnerReady: defaultLoadableState(false),
  openHotspot() {},
  openKeyFeatures() {},
  setIsSpinnerImmersive: () => false,
  setOpenHotspot() {},
  setOpenKeyFeatures() {}
};

const GlobalContext = createContext(defaultContext);

type OwnProps = PropsWithChildren & {
  giveawayIsLive: boolean;
};

export function GlobalContextProvider({ children, giveawayIsLive }: OwnProps) {
  const isSpinnerReady = useLoadableState(false);
  const [isSpinnerImmersive, setIsSpinnerImmersive] = useState<boolean>(false);
  const [openHotspot, setOpenHotspot] = useState<DummyFnType>(
    () => defaultContext.openHotspot
  );
  const [openKeyFeatures, setOpenKeyFeatures] = useState<DummyFnType>(
    () => defaultContext.openKeyFeatures
  );

  const value = useMemo(
    () => ({
      giveawayIsLive,
      isSpinnerImmersive,
      isSpinnerReady,
      openHotspot,
      openKeyFeatures,
      setIsSpinnerImmersive,
      setOpenHotspot,
      setOpenKeyFeatures
    }),
    [
      giveawayIsLive,
      isSpinnerImmersive,
      isSpinnerReady,
      openHotspot,
      openKeyFeatures
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export default GlobalContext;
